// The single mutation pipeline for the v2 planner. Every state change flows
// through exactly one of two entry points, so "forgot to push undo / mark
// dirty" is not a possible bug class:
//
//   apply(mutator, args)      — pure (state, args) => patch mutators (model/mutators/*)
//   commitGesture(prev, fn?)  — canvas gestures where Konva already mutated the
//                               live geometry; `prev` was snapshotted at gesture
//                               start (drag/transform/vertex edits)
//
// applyTransient() is the audited escape hatch for continuous inputs: state +
// dirty, but no undo entry per tick.

import UndoStack from './undo';
import { snapshot } from './serialize';

export default class Editor {
  constructor(store, { onDirty, onHistoryChange } = {}) {
    this.store = store;
    this.undoStack = new UndoStack();
    this.onDirty = onDirty || (() => {});
    this.onHistoryChange = onHistoryChange || (() => {});
  }

  takeSnapshot() { return snapshot(this.store.state); }

  // Returns true if the mutator changed anything (null patch = no-op, no undo entry).
  apply(mutator, args) {
    const prev = this.takeSnapshot();
    const patch = mutator(this.store.state, args);
    if (!patch) return false;
    this.undoStack.push(prev);
    this.store.set(patch, { source: 'commit' });
    this.onDirty();
    this.onHistoryChange();
    return true;
  }

  // Same pipeline without an undo entry — for continuous inputs.
  applyTransient(mutator, args) {
    const patch = mutator(this.store.state, args);
    if (!patch) return false;
    this.store.set(patch, { source: 'commit' });
    this.onDirty();
    return true;
  }

  commitGesture(prevSnapshot, fn) {
    if (fn) fn();
    this.undoStack.push(prevSnapshot);
    this.store.emit({ source: 'commit' });
    this.onDirty();
    this.onHistoryChange();
  }

  canUndo() { return this.undoStack.canUndo(); }
  canRedo() { return this.undoStack.canRedo(); }

  undo() {
    if (!this.undoStack.canUndo()) return;
    this.restore(this.undoStack.undo(this.takeSnapshot()));
  }

  redo() {
    if (!this.undoStack.canRedo()) return;
    this.restore(this.undoStack.redo(this.takeSnapshot()));
  }

  // Undo/redo only swap the document back; the selection stays as it is,
  // minus anything the restored snapshot no longer has (or has locked).
  restore(s) {
    if (!s) return;
    const selectable = new Set(s.elements.filter((el) => !el.data?.locked).map((el) => el.id));
    // A picked segment survives only if its path kept its points (an undone
    // add/remove point renumbers the segments).
    const pick = this.store.state.selectedSegment;
    const count = (els) => els.find((el) => el.id === pick?.id)?.data?.coordinates?.length;
    const selectedSegment = pick && count(s.elements) === count(this.store.state.elements) ? pick : null;
    this.store.set(
      { elements: s.elements, layers: s.layers, settings: s.settings, selection: this.store.state.selection.filter((id) => selectable.has(id)), selectedSegment },
      { source: 'undo' },
    );
    this.onDirty();
    this.onHistoryChange();
  }
}
